import {
  assign,
  createStateMachine,
  type InvokeDoneEvent,
} from '@heidi/flow/state-machine';

export interface ChronicleStartupDevice {
  id: string;
  deviceName: string | null;
  firmwareVersion: string | null;
  serialNumber: string | null;
}

interface ChronicleStartupAutoConnectContext {
  device: ChronicleStartupDevice | null;
  bindingCode: string | undefined;
  confirmedBindingCode: string | undefined;
}

const initialContext: ChronicleStartupAutoConnectContext = {
  device: null,
  bindingCode: undefined,
  confirmedBindingCode: undefined,
};

const resetContext = assign<ChronicleStartupAutoConnectContext>(() => initialContext);

/**
 * Best-effort Chronicle BLE auto-connect on app startup.
 * Failures at any phase land in `done` — startup must never block the app.
 */
export const chronicleStartupAutoConnectMachine = createStateMachine({
  id: 'chronicleStartupAutoConnect',
  initial: 'idle',
  context: initialContext,
  states: {
    idle: {
      on: {
        START: {
          target: 'fetchingDevices',
          actions: resetContext,
        },
      },
    },
    fetchingDevices: {
      on: { RESET: { target: 'idle', actions: resetContext } },
      invoke: {
        src: 'fetchDevices',
        onDone: {
          target: 'resolvingBindingCode',
          actions: assign<
            ChronicleStartupAutoConnectContext,
            InvokeDoneEvent<ChronicleStartupDevice>
          >((_, event) => ({
            device: event.data,
          })),
        },
        onError: 'done',
      },
    },
    resolvingBindingCode: {
      on: { RESET: { target: 'idle', actions: resetContext } },
      invoke: {
        src: 'resolveBindingCode',
        onDone: {
          target: 'connecting',
          actions: assign<
            ChronicleStartupAutoConnectContext,
            InvokeDoneEvent<string | undefined>
          >((_, event) => ({
            bindingCode: event.data,
          })),
        },
        onError: 'connecting',
      },
    },
    connecting: {
      on: { RESET: { target: 'idle', actions: resetContext } },
      invoke: {
        src: 'connect',
        onDone: {
          target: 'syncingBindingCode',
          actions: assign<
            ChronicleStartupAutoConnectContext,
            InvokeDoneEvent<string | undefined>
          >((_, event) => ({
            confirmedBindingCode: event.data,
          })),
        },
        onError: 'done',
      },
    },
    syncingBindingCode: {
      on: { RESET: { target: 'idle', actions: resetContext } },
      invoke: {
        src: 'syncBindingCode',
        onDone: 'done',
        onError: 'done',
      },
    },
    done: {
      type: 'final',
      on: { RESET: { target: 'idle', actions: resetContext } },
    },
  },
});
